/* ============================================================================
   Dot-grid number rain
   ----------------------------------------------------------------------------
   Drops random numbers all over the page, snapped to the dot grid. Each number
   is "drawn" stroke-by-stroke along its lines (pen reveal), holds, then fades.

   Drop-in usage:
     1. import { setupFX } from './fx-engine.ts'; setupFX();  (injects its own
        full-page canvas that draws BOTH the dot grid and the numbers).
     2. Tweak live via window.FX. Set FX.bg='' and FX.drawGrid=false to render
        numbers transparently over your own background.
   ============================================================================ */

/** A 2D coordinate pair [x, y] in glyph or pixel space. */
export type Point = [number, number];

/** An ordered list of points forming one continuous polyline stroke. */
export type Stroke = Point[];

/** All strokes that compose a single digit glyph. */
export type GlyphStrokes = Stroke[];

/** Maps font characters to their stroke definitions. */
export type FontMap = Record<string, GlyphStrokes>;

/* ── Glyph font ─────────────────────────────────────────────────────────────
   Each digit occupies a logical cell that is 2 units wide × 3 units tall.
   Coordinates: x ∈ {0, 1, 2}, y ∈ {0, 1.5, 3}.  1.5 is the mid-row bar.
   Entries are lists of polyline strokes (not filled shapes).               */
// biome-ignore format: the array should not be formatted
export const FONT = {
  '0': [[[0,0],[2,0],[2,3],[0,3],[0,0]]],
  '1': [[[0,1.5],[2,0],[2,3]]],
  '2': [[[0,0],[2,0],[2,1.5],[0,3],[2,3]]],
  '3': [[[0,0],[2,0],[0,1.5],[2,1.5],[0,3]]],
  '4': [[[0,0],[0,1.5],[2,1.5]],[[2,0],[2,3]]],
  '5': [[[2,0],[0,0],[0,1.5],[2,1.5],[2,3],[0,3]]],
  '6': [[[2,0],[0,1.5],[0,3],[2,3],[2,1.5],[0,1.5]]],
  '7': [[[0,0],[2,0],[0,1.5],[0,3]]],
  '8': [[[0,0],[2,0],[2,3],[0,3],[0,0]],[[0,1.5],[2,1.5]]],
  '9': [[[0,3],[2,1.5],[2,0],[0,0],[0,1.5],[2,1.5]]],
} satisfies FontMap;

/** Characters that have a glyph defined in FONT. */
export type FontKey = keyof typeof FONT;

/** Every character available in FONT, used as the default character pool. */
export const FONT_KEYS = Object.keys(FONT) as FontKey[];

/** Runtime state for a single number currently animating on screen. */
export interface NumberInstance {
	/** Pixel-space polylines built from the glyph font, ready for ctx.stroke(). */
	strokes: Stroke[];
	/** Sum of all segment lengths in pixels, used to drive the reveal animation. */
	totalPathLength: number;
	/** Base line width before the global thickness multiplier is applied. */
	lineWidth: number;
	/** Pixel width of the number's bounding box, used for collision avoidance. */
	widthPixels: number;
	/** Pixel height of the number's bounding box, used for collision avoidance. */
	heightPixels: number;
	/** Horizontal centre of the bounding box, used for collision avoidance. */
	centerX: number;
	/** Vertical centre of the bounding box, used for collision avoidance. */
	centerY: number;
	/** Milliseconds elapsed since this instance was spawned. */
	age: number;
	/** Per-instance draw duration, jittered from FX.drawMs. */
	drawMs: number;
	/** Per-instance hold duration, jittered from FX.holdMs. */
	holdMs: number;
	/** Per-instance fade duration, jittered from FX.fadeMs. */
	fadeMs: number;
	/** Peak opacity for this instance, randomised slightly for visual variety. */
	baseAlpha: number;
}

/** All tuneable parameters for the dot-grid animation. Mutate window.FX at runtime to change behaviour. */
export interface FXConfig {
	/** Grid spacing in pixels (distance between dot centres). */
	dot: number;
	/** Background fill colour drawn on the canvas. Empty string = transparent. */
	bg: string;
	/** Whether to draw the dot grid on the canvas itself. */
	drawGrid: boolean;
	/** Dot tint colour. */
	dotColor: string;
	/** Dot radius in pixels. */
	dotRadius: number;
	/** Number stroke colour. */
	color: string;
	/** Milliseconds between number spawns. */
	spawnMs: number;
	/** Minimum digit cell width in grid dots. */
	cellWMin: number;
	/** Maximum digit cell width in grid dots. */
	cellWMax: number;
	/** Legacy uniform cell-width alias, read when cellWMin/cellWMax are absent. */
	cellW?: number;
	/** Maximum digits per spawned number (actual count is random in 1..maxDigits). */
	maxDigits: number;
	/** Pool of characters eligible for selection when spawning a number. Each must be a key in FONT. null = use every character in FONT. */
	allowedCharacters: FontKey[] | null;
	/** When true, characters are picked from allowedCharacters in order, advancing and wrapping. When false (default), each character is picked at random from allowedCharacters. */
	sequential: boolean;
	/** Duration of the pen-draw reveal phase in milliseconds. */
	drawMs: number;
	/** Duration of the full-opacity hold phase in milliseconds. */
	holdMs: number;
	/** Duration of the fade-out phase in milliseconds. */
	fadeMs: number;
	/** Canvas shadow blur radius in pixels, producing a glow effect around strokes. */
	glow: number;
	/** Multiplier applied to the computed line width. */
	thickness: number;
}

declare global {
	interface Window {
		FX: Partial<FXConfig>;
	}
}

export function setupFX(): void {
	/* ── Live config ─────────────────────────────────────────────────────────────
     applyFXConfig() (from fx-config-base.ts) pre-populates window.FX before
     this runs. Any field it leaves unset falls back to defaultConfig below,
     so the engine also works stand-alone with defaults only.                */
	const defaultConfig: FXConfig = {
		bg: "#000000",
		dotColor: "#303030",
		color: "#ffffff",

		drawGrid: true, // canvas draws its own dot grid

		/* grid ------------------------------------------------------------------ */
		dot: 16, // grid spacing in px
		dotRadius: 1, // dot radius in px

		/* numbers --------------------------------------------------------------- */
		cellWMin: 2, // min digit width in dots
		cellWMax: 5, // max digit width in dots
		maxDigits: 1, // digits per spawned number
		allowedCharacters: null,
		sequential: false,

		/* animation ------------------------------------------------------------- */
		spawnMs: 520, // ms between spawns
		drawMs: 750, // pen-draw duration
		holdMs: 600, // full-opacity hold
		fadeMs: 1200, // fade-out duration

		/* appearance ------------------------------------------------------------ */
		glow: 0, // canvas shadow blur in px (canvas-specific, not a CSS token)
		thickness: 1, // line-width multiplier
	};

	// Fields left unset by the page's applyFXConfig() call fall back to defaultConfig
	const FX = { ...defaultConfig, ...window.FX } as FXConfig;
	window.FX = FX;

	/* ── Canvas setup ───────────────────────────────────────────────────────── */
	const canvas = document.createElement("canvas");
	canvas.id = "fx-numbers";
	canvas.style.cssText =
		"position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:0;";

	function mountCanvas(): void {
		(document.body ?? document.documentElement).appendChild(canvas);
	}
	if (document.body) mountCanvas();
	else document.addEventListener("DOMContentLoaded", mountCanvas);

	function require2DContext(el: HTMLCanvasElement): CanvasRenderingContext2D {
		const ctx = el.getContext("2d");
		if (!ctx) throw new Error("Canvas 2D context unavailable");
		return ctx;
	}

	const canvasContext = require2DContext(canvas);
	let viewportWidth = 0;
	let viewportHeight = 0;
	let pixelRatio = 1;

	function handleResize(): void {
		pixelRatio = Math.min(window.devicePixelRatio ?? 1, 2);
		viewportWidth = window.innerWidth;
		viewportHeight = window.innerHeight;
		canvas.width = Math.round(viewportWidth * pixelRatio);
		canvas.height = Math.round(viewportHeight * pixelRatio);
		canvasContext.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
	}
	window.addEventListener("resize", handleResize);
	handleResize();

	/* ── Offscreen grid canvas ──────────────────────────────────────────────────
     The dot grid is expensive to redraw (hundreds of arc() calls) but changes
     rarely — only on resize or when a grid-related FX property is mutated.
     We draw it once into an offscreen canvas and blit it with drawImage()
     each frame, which is a single GPU copy rather than re-issuing every arc.  */
	const offscreenGridCanvas = document.createElement("canvas");
	const gridContext = require2DContext(offscreenGridCanvas);
	let gridCacheKey = "";

	function renderGridToOffscreen(): void {
		offscreenGridCanvas.width = canvas.width;
		offscreenGridCanvas.height = canvas.height;
		gridContext.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
		gridContext.clearRect(0, 0, viewportWidth, viewportHeight);

		if (FX.bg) {
			gridContext.fillStyle = FX.bg;
			gridContext.fillRect(0, 0, viewportWidth, viewportHeight);
		}

		if (FX.drawGrid !== false) {
			const gridSpacing = FX.dot;
			const dotRadius = FX.dotRadius ?? 1.2;
			gridContext.fillStyle = FX.dotColor ?? "#303030";

			// Offset by half a grid step so dots are centred within each cell
			for (
				let y = gridSpacing * 0.5;
				y < viewportHeight + gridSpacing;
				y += gridSpacing
			) {
				for (
					let x = gridSpacing * 0.5;
					x < viewportWidth + gridSpacing;
					x += gridSpacing
				) {
					gridContext.beginPath();
					gridContext.arc(x, y, dotRadius, 0, Math.PI * 2);
					gridContext.fill();
				}
			}
		}
	}

	/** Returns a string that changes whenever any property affecting the grid changes. */
	function computeGridCacheKey(): string {
		return [
			viewportWidth,
			viewportHeight,
			pixelRatio,
			FX.dot,
			FX.bg,
			FX.drawGrid,
			FX.dotColor,
			FX.dotRadius,
		].join("|");
	}

	/* ── Helpers ─────────────────────────────────────────────────────────────── */
	function randomRange(minimum: number, maximum: number): number {
		return minimum + Math.random() * (maximum - minimum);
	}

	function easeOutCubic(t: number): number {
		return 1 - (1 - t) ** 3;
	}

	/* ── Glyph builder ──────────────────────────────────────────────────────────
     Converts a digit string into pixel-space polyline strokes.

     The glyph coordinate system maps onto the dot grid such that every stroke
     endpoint lands exactly on a dot centre:
       scaleX = (cellWidth  − 1) × gridSpacing / 2   (glyph x spans 0 → 2)
       scaleY = (cellHeight − 1) × gridSpacing / 3   (glyph y spans 0 → 3)

     cellHeight is always (2 × cellWidth − 1), giving an exact 1:2 aspect ratio
     because the glyph spans 2 column-gaps wide and 3 row-gaps tall (2:3 gap
     ratio → 1:1.5 cell ratio → same as the 2-wide × 3-tall glyph grid).       */
	function buildGlyphStrokes(
		digitString: string,
		originX: number,
		originY: number,
		cellWidth: number,
		cellHeight: number,
		gridSpacing: number,
	): Pick<NumberInstance, "strokes" | "totalPathLength"> {
		const scaleX = ((cellWidth - 1) * gridSpacing) / 2;
		const scaleY = ((cellHeight - 1) * gridSpacing) / 3;
		const digitAdvancePixels = cellWidth * gridSpacing; // horizontal step per digit incl. 1-dot gap

		const allStrokes: NumberInstance["strokes"] = [];
		let totalPathLength = 0;

		for (let digitIndex = 0; digitIndex < digitString.length; digitIndex++) {
			const glyphStrokes = FONT[digitString[digitIndex] as FontKey];
			const digitOffsetX = digitIndex * digitAdvancePixels;

			for (const glyphStroke of glyphStrokes) {
				const pixelPoints = glyphStroke.map(
					([glyphX, glyphY]) =>
						[
							originX + digitOffsetX + glyphX * scaleX,
							originY + glyphY * scaleY,
						] as [number, number],
				);

				let strokeLength = 0;
				for (
					let pointIndex = 1;
					pointIndex < pixelPoints.length;
					pointIndex++
				) {
					strokeLength += Math.hypot(
						pixelPoints[pointIndex][0] - pixelPoints[pointIndex - 1][0],
						pixelPoints[pointIndex][1] - pixelPoints[pointIndex - 1][1],
					);
				}

				allStrokes.push(pixelPoints);
				totalPathLength += strokeLength;
			}
		}

		return { strokes: allStrokes, totalPathLength };
	}

	/* ── Active instances ───────────────────────────────────────────────────── */
	const activeInstances: NumberInstance[] = [];
	let sequenceIndex = 0;

	function spawnNumberInstance(): void {
		const gridSpacing = FX.dot;
		const minCellWidth = Math.max(2, Math.round(FX.cellWMin ?? FX.cellW ?? 3));
		const maxCellWidth = Math.max(
			minCellWidth,
			Math.round(FX.cellWMax ?? minCellWidth),
		);

		const cellWidth =
			minCellWidth + ((Math.random() * (maxCellWidth - minCellWidth + 1)) | 0);
		/* Height formula: (2 × width − 1) dots ensures the cell spans exactly
       (width − 1) column-gaps wide and (height − 1) = (2width − 2) row-gaps tall,
       matching the 1:2 aspect ratio of the glyph coordinate grid (2 cols : 3 rows → 2:3 gaps → 1:1.5 → ×2 = 1:2). */
		const cellHeight = 2 * cellWidth - 1;

		/* Choose digit string from the active character pool. */
		const digitCount = Math.max(
			1,
			Math.min(FX.maxDigits, 1 + ((Math.random() * FX.maxDigits) | 0)),
		);
		const characterPool =
			FX.allowedCharacters && FX.allowedCharacters.length > 0
				? FX.allowedCharacters
				: FONT_KEYS;

		let digitString: string;
		if (FX.sequential) {
			const characters: string[] = [];
			for (let i = 0; i < digitCount; i++) {
				characters.push(characterPool[sequenceIndex % characterPool.length]);
				sequenceIndex++;
			}
			digitString = characters.join("");
		} else {
			digitString = Array.from(
				{ length: digitCount },
				() => characterPool[(Math.random() * characterPool.length) | 0],
			).join("");
		}
		if (!digitString) return;

		/* Compute bounding box in pixels. */
		const digitAdvancePixels = cellWidth * gridSpacing;
		const widthPixels =
			(digitString.length - 1) * digitAdvancePixels +
			(cellWidth - 1) * gridSpacing;
		const heightPixels = (cellHeight - 1) * gridSpacing;

		// Bail early if the number would not fit with at least one dot of padding on each edge
		if (widthPixels + gridSpacing * 2 > viewportWidth) return;
		if (heightPixels + gridSpacing * 2 > viewportHeight) return;

		/* Snap the origin to a dot centre. Because advances are whole-dot multiples,
       all glyph corners automatically land on dot centres.                      */
		const maxGridColumn = Math.floor(
			(viewportWidth - widthPixels - gridSpacing) / gridSpacing,
		);
		const maxGridRow = Math.floor(
			(viewportHeight - heightPixels - gridSpacing) / gridSpacing,
		);
		if (maxGridColumn < 0 || maxGridRow < 0) return;

		let originX = 0,
			originY = 0,
			centerX = 0,
			centerY = 0;
		let placementSucceeded = false;
		let placementAttempts = 0;

		/* Try up to 24 random grid positions, rejecting any that overlap a
       currently-visible instance (drawing, holding, or fading — all are still
       on screen). Padding adds one dot of breathing room beyond a bare touch. */
		const placementPadding = gridSpacing;
		do {
			const gridColumn = (Math.random() * (maxGridColumn + 1)) | 0;
			const gridRow = (Math.random() * (maxGridRow + 1)) | 0;
			originX = gridColumn * gridSpacing + gridSpacing * 0.5;
			originY = gridRow * gridSpacing + gridSpacing * 0.5;
			centerX = originX + widthPixels / 2;
			centerY = originY + heightPixels / 2;

			placementSucceeded = true;
			for (const existingInstance of activeInstances) {
				/* True AABB overlap test using each instance's own half-width and
           half-height (not a single width-derived value for both axes). */
				const xOverlap =
					Math.abs(existingInstance.centerX - centerX) <
					(existingInstance.widthPixels + widthPixels) / 2 + placementPadding;
				const yOverlap =
					Math.abs(existingInstance.centerY - centerY) <
					(existingInstance.heightPixels + heightPixels) / 2 + placementPadding;
				if (xOverlap && yOverlap) {
					placementSucceeded = false;
					break;
				}
			}
		} while (!placementSucceeded && ++placementAttempts < 24);

		if (!placementSucceeded) return;

		const { strokes, totalPathLength } = buildGlyphStrokes(
			digitString,
			originX,
			originY,
			cellWidth,
			cellHeight,
			gridSpacing,
		);

		activeInstances.push({
			strokes,
			totalPathLength,
			lineWidth: Math.max(1.5, gridSpacing * 0.1 * (cellWidth / 3)),
			widthPixels,
			heightPixels,
			centerX,
			centerY,
			age: 0,
			drawMs: FX.drawMs * randomRange(0.85, 1.15),
			holdMs: FX.holdMs * randomRange(0.8, 1.2),
			fadeMs: FX.fadeMs * randomRange(0.85, 1.15),
			baseAlpha: randomRange(0.8, 1),
		});
	}

	/* ── Stroke reveal ──────────────────────────────────────────────────────────
     Draws `revealFraction` (0..1) of the instance's total path length.
     Strokes are traversed in order. The final partially-covered segment is
     clipped by linear interpolation so the pen appears to move continuously.  */
	function drawGlyphReveal(
		instance: NumberInstance,
		revealFraction: number,
	): void {
		if (revealFraction >= 1) {
			// Fast path: all strokes are fully visible
			for (const strokePoints of instance.strokes) {
				canvasContext.beginPath();
				canvasContext.moveTo(strokePoints[0][0], strokePoints[0][1]);
				for (
					let pointIndex = 1;
					pointIndex < strokePoints.length;
					pointIndex++
				) {
					canvasContext.lineTo(
						strokePoints[pointIndex][0],
						strokePoints[pointIndex][1],
					);
				}
				canvasContext.stroke();
			}
			return;
		}

		const targetPathLength = revealFraction * instance.totalPathLength;
		let accumulatedLength = 0;

		outer: for (const strokePoints of instance.strokes) {
			canvasContext.beginPath();
			canvasContext.moveTo(strokePoints[0][0], strokePoints[0][1]);

			for (let pointIndex = 1; pointIndex < strokePoints.length; pointIndex++) {
				const segmentStart = strokePoints[pointIndex - 1];
				const segmentEnd = strokePoints[pointIndex];
				const segmentLength = Math.hypot(
					segmentEnd[0] - segmentStart[0],
					segmentEnd[1] - segmentStart[1],
				);

				if (accumulatedLength + segmentLength <= targetPathLength) {
					canvasContext.lineTo(segmentEnd[0], segmentEnd[1]);
					accumulatedLength += segmentLength;
				} else {
					// Interpolate to the exact point where the reveal front sits
					const segmentProgress =
						segmentLength > 0
							? Math.max(
									0,
									(targetPathLength - accumulatedLength) / segmentLength,
								)
							: 0;
					canvasContext.lineTo(
						segmentStart[0] +
							(segmentEnd[0] - segmentStart[0]) * segmentProgress,
						segmentStart[1] +
							(segmentEnd[1] - segmentStart[1]) * segmentProgress,
					);
					canvasContext.stroke();
					break outer; // reveal front is within this segment — stop drawing
				}
			}

			canvasContext.stroke();
		}
	}

	/* ── Animation loop ─────────────────────────────────────────────────────── */
	let lastFrameTime = performance.now();
	let spawnAccumulator = 0;

	function animationFrame(now: number): void {
		/* Cap delta to 60 ms so a tab becoming visible after being hidden doesn't
       trigger a burst of back-logged spawns.                                   */
		const deltaTime = Math.min(now - lastFrameTime, 60);
		lastFrameTime = now;

		canvasContext.clearRect(0, 0, viewportWidth, viewportHeight);

		// Blit the cached grid, rebuilding it lazily only when a grid property changes
		const currentGridKey = computeGridCacheKey();
		if (currentGridKey !== gridCacheKey) {
			gridCacheKey = currentGridKey;
			renderGridToOffscreen();
		}
		if (FX.drawGrid !== false || FX.bg) {
			canvasContext.drawImage(
				offscreenGridCanvas,
				0,
				0,
				viewportWidth,
				viewportHeight,
			);
		}

		/* Accumulate elapsed time and spawn as many instances as the interval allows.
       The guard prevents an infinite loop if spawnMs is set to 0 or near-zero.  */
		spawnAccumulator += deltaTime;
		let spawnLoopGuard = 0;
		// Derived from how many full lifecycles fit in flight at the current spawn cadence.
		const effectiveMax = Math.ceil(
			(FX.drawMs + FX.holdMs + FX.fadeMs) / FX.spawnMs,
		);
		while (spawnAccumulator >= FX.spawnMs && spawnLoopGuard++ < 64) {
			spawnAccumulator -= FX.spawnMs;
			if (activeInstances.length < effectiveMax) spawnNumberInstance();
		}
		if (spawnLoopGuard >= 64) spawnAccumulator = 0; // safety reset

		canvasContext.lineJoin = "miter";
		canvasContext.lineCap = "round";
		canvasContext.strokeStyle = FX.color;
		canvasContext.shadowColor = FX.color;

		// Traverse backwards so splice() inside the loop doesn't skip entries
		for (
			let instanceIndex = activeInstances.length - 1;
			instanceIndex >= 0;
			instanceIndex--
		) {
			const instance = activeInstances[instanceIndex];
			instance.age += deltaTime;

			let currentAlpha: number;
			let revealFraction: number;

			if (instance.age < instance.drawMs) {
				revealFraction = easeOutCubic(instance.age / instance.drawMs);
				// Ramp opacity in quickly at the very start to avoid a harsh pop-in
				currentAlpha = Math.min(1, instance.age / 120);
			} else if (instance.age < instance.drawMs + instance.holdMs) {
				revealFraction = 1;
				currentAlpha = 1;
			} else {
				const fadeProgress =
					(instance.age - instance.drawMs - instance.holdMs) / instance.fadeMs;
				if (fadeProgress >= 1) {
					activeInstances.splice(instanceIndex, 1);
					continue;
				}
				revealFraction = 1;
				currentAlpha = 1 - fadeProgress;
			}

			canvasContext.globalAlpha = currentAlpha * instance.baseAlpha;
			canvasContext.lineWidth = Math.max(
				1.5,
				instance.lineWidth * FX.thickness,
			);
			canvasContext.shadowBlur = FX.glow;
			drawGlyphReveal(instance, revealFraction);
		}

		// Reset state that persists across draw calls
		canvasContext.globalAlpha = 1;
		canvasContext.shadowBlur = 0;

		requestAnimationFrame(animationFrame);
	}

	requestAnimationFrame(animationFrame);
}
